import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Botao } from './Botao.tsx';
import { Campo } from './Campo.tsx';

type Props = {
  aberto: boolean;
  titulo: string;
  children?: ReactNode;
  aoFechar: () => void;
};

/**
 * Modal acessível: prende o foco enquanto aberto, fecha no Escape e devolve o
 * foco a quem o abriu. Substitui `window.confirm`/`prompt`, que não aceitam
 * estilo, travam a aba inteira e destoam do resto da interface.
 */
export function Dialogo({ aberto, titulo, children, aoFechar }: Props) {
  const painel = useRef<HTMLDivElement>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!aberto) return;

    focoAnterior.current = document.activeElement as HTMLElement;

    // Foca o primeiro campo, ou o painel se não houver nenhum.
    const alvo = painel.current?.querySelector<HTMLElement>(
      'input, textarea, button:not([data-secundario])',
    );
    (alvo ?? painel.current)?.focus();

    // Trava a rolagem do fundo enquanto o modal está aberto.
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        aoFechar();
        return;
      }

      if (e.key !== 'Tab' || !painel.current) return;

      // Ciclo de foco: Tab no último volta ao primeiro, e vice-versa.
      const focaveis = painel.current.querySelectorAll<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
      );
      if (!focaveis.length) return;

      const primeiro = focaveis[0]!;
      const ultimo = focaveis[focaveis.length - 1]!;

      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    }

    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
      focoAnterior.current?.focus();
    };
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  return (
    <div
      className="dialogo-fundo"
      // Clique no fundo fecha; clique dentro do painel não sobe até aqui.
      onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}
    >
      <div
        ref={painel}
        className="dialogo"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialogo-titulo"
        tabIndex={-1}
      >
        <h2 className="dialogo__titulo" id="dialogo-titulo">
          {titulo}
        </h2>
        {children}
      </div>
    </div>
  );
}

type Pedido = {
  titulo: string;
  texto?: string;
  /** Presente = pede texto ao usuário; ausente = só confirma. */
  campo?: { rotulo: string; dica?: string; obrigatorio?: boolean };
  confirmar?: string;
  perigo?: boolean;
  aoConfirmar: (texto: string) => void | Promise<void>;
};

/**
 * Hook que devolve `perguntar()` e o elemento do diálogo.
 *
 *   const { perguntar, dialogo } = useDialogo();
 *   perguntar({ titulo: 'Revogar acesso?', aoConfirmar: () => ... });
 *   return <>{dialogo}...</>;
 */
export function useDialogo() {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const perguntar = useCallback((p: Pedido) => {
    setTexto('');
    setPedido(p);
  }, []);

  const fechar = useCallback(() => {
    if (!ocupado) setPedido(null);
  }, [ocupado]);

  async function confirmar() {
    if (!pedido) return;
    setOcupado(true);
    try {
      await pedido.aoConfirmar(texto);
      setPedido(null);
    } finally {
      setOcupado(false);
    }
  }

  const dialogo = (
    <Dialogo aberto={!!pedido} titulo={pedido?.titulo ?? ''} aoFechar={fechar}>
      <div className="pilha">
        {pedido?.texto && <p className="dialogo__texto">{pedido.texto}</p>}

        {pedido?.campo && (
          <Campo
            rotulo={pedido.campo.rotulo}
            dica={pedido.campo.dica}
            opcional={!pedido.campo.obrigatorio}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void confirmar()}
          />
        )}

        <div className="dialogo__acoes">
          <Botao variante="fantasma" onClick={fechar} disabled={ocupado} data-secundario>
            Cancelar
          </Botao>
          <Botao
            variante={pedido?.perigo ? 'perigo' : 'primario'}
            onClick={() => void confirmar()}
            carregando={ocupado}
            disabled={pedido?.campo?.obrigatorio && !texto.trim()}
          >
            {pedido?.confirmar ?? 'Confirmar'}
          </Botao>
        </div>
      </div>
    </Dialogo>
  );

  return { perguntar, dialogo };
}
